import React, { useState, useEffect, useRef, useMemo } from 'react'
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Activity,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  LogOut,
  X,
  Clock,
  Shield,
  Play,
  Pause,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { portfolioAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import SortableTable from '../components/SortableTable'
import { useAuth } from '../contexts/AuthContext'
import { useMarketAutomation } from '../contexts/MarketAutomationContext'
import { openTradingView } from '../utils/tradingView'
import { stockDataAPI } from '../services/api'
import toast from 'react-hot-toast'
import { recommendationsAPI } from '../services/api';
import { marginAPI } from '../services/api';
import { useStreaming } from '../contexts/StreamingContext';
import { watchlistAPI } from '../services/api';
import { kiteAPI } from '../services/api';
import { slOrdersAPI } from '../services/api';

const IntradayRobust = () => {
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const { user } = useAuth()
  const { lastSignalGeneration } = useMarketAutomation()
  const { startStreaming, isStreaming } = useStreaming();
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [exitingPosition, setExitingPosition] = useState(null)
  const [cancellingOrder, setCancellingOrder] = useState(null)
  const [autoManagementActive, setAutoManagementActive] = useState(false)
  const [intradayLogs, setIntradayLogs] = useState([])
  const [data, setData] = useState({
    margins: null,
    positions: [],
    orders: [],
    dayPnL: 0
  })

  // --- Actions/Recommendations state ---
  const [minutes, setMinutes] = useState(1);
  const [growth, setGrowth] = useState([]);
  const [fall, setFall] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [geminiLoading, setGeminiLoading] = useState(false);
  const [geminiResult, setGeminiResult] = useState(null);
  const [geminiCooldown, setGeminiCooldown] = useState(0);
  const [geminiLastRun, setGeminiLastRun] = useState(null);

  // --- ROBUST STATE MANAGEMENT ---
  const [orderHistory, setOrderHistory] = useState(new Set()); // Track placed orders
  const [lastOrderTime, setLastOrderTime] = useState({}); // Prevent rapid orders
  const [isProcessingOrder, setIsProcessingOrder] = useState(false); // Prevent concurrent orders
  const [validationErrors, setValidationErrors] = useState([]);

  const intervalRef = useRef(null)
  const autoManagementRef = useRef(null)
  const isCurrentlyFetching = useRef(false)
  const GEMINI_AUTOBUY_INTERVAL = 2 * 60 * 1000; // 2 minutes
  const MARGIN_MULTIPLIER = 3; // Use 3x margin for MIS order
  const lastGeminiBuyRef = useRef({}); // { symbol: timestamp }
  const orderCooldownRef = useRef({}); // { symbol: timestamp }

  // --- ROBUST CONSTANTS ---
  const MIN_ORDER_INTERVAL = 30 * 1000; // 30 seconds between orders for same symbol
  const MAX_CONCURRENT_ORDERS = 3; // Maximum concurrent orders
  const PROFIT_THRESHOLD = 0.002; // 0.2% profit for auto-exit
  const STOP_LOSS_PERCENTAGE = 0.5; // 0.5% stop loss
  const AUTO_CANCEL_MINUTES = 20; // Auto-cancel orders after 20 minutes

  // --- STREAMING RECOMMENDATIONS & GEMINI INTEGRATION ---
  const [streamingRecommendations, setStreamingRecommendations] = useState({ growth: [], fall: [] });
  const [lastRecommendationFetch, setLastRecommendationFetch] = useState(null);
  const [recommendationInterval, setRecommendationInterval] = useState(60); // seconds - default 1 minute
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const recommendationIntervalRef = useRef(null);
  const autoOrderIntervalRef = useRef(null);
  const [autoOrderEnabled, setAutoOrderEnabled] = useState(false);
  const [lastAutoOrderTime, setLastAutoOrderTime] = useState(null);
  
  // --- RECOMMENDATION FETCH INTERVAL ---
  const [fetchInterval, setFetchInterval] = useState(1); // minutes - default 1 minute
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // --- MARGIN CALCULATOR ---
  const calculateOrderQuantity = (ltp, maxOrderAmount) => {
    if (!ltp || ltp <= 0 || !maxOrderAmount || maxOrderAmount <= 0) {
      addLog(`❌ Invalid inputs for quantity calculation: LTP=${ltp}, MaxAmount=${maxOrderAmount}`, 'error');
      return 0;
    }
    
    // Calculate quantity based on LTP and max order amount
    const quantity = Math.floor(maxOrderAmount / ltp);
    
    addLog(`📊 Quantity calculation: MaxAmount=₹${maxOrderAmount}, LTP=₹${ltp}, Quantity=${quantity}`, 'info');
    
    // Ensure minimum quantity of 1
    return Math.max(quantity, 1);
  }

  // Helper function to check if margin data is available
  const ensureMarginData = async () => {
    if (!data.margins?.equity?.available) {
      addLog('🔄 Margin data not available, fetching fresh data...', 'info');
      await fetchIntradayData();
      
      // Wait a bit and check again
      setTimeout(() => {
        if (!data.margins?.equity?.available) {
          addLog('⚠️ Still no margin data available after fetch', 'warning');
        }
      }, 1000);
    }
  }

  // Memoized margin calculations to prevent infinite re-renders
  const marginCalculations = useMemo(() => {
    // Extract available margin from the complex structure
    let availableMargin = 0;
    
    if (data.margins?.equity?.available) {
      // Try different possible fields for available margin
      if (typeof data.margins.equity.available === 'number') {
        availableMargin = data.margins.equity.available;
      } else if (data.margins.equity.available.live_balance) {
        availableMargin = Number(data.margins.equity.available.live_balance);
      } else if (data.margins.equity.available.cash) {
        availableMargin = Number(data.margins.equity.available.cash);
      } else if (data.margins.equity.available.opening_balance) {
        availableMargin = Number(data.margins.equity.available.opening_balance);
      }
    }
    
    // Use 5X margin boost for more aggressive trading
    const marginMultiplier = 5;
    const boostedMargin = availableMargin * marginMultiplier;
    
    // Use 20% of boosted margin per order for more aggressive position sizing
    const orderAmount = Math.max(boostedMargin * 0.2, 2000);
    
    return {
      availableMargin,
      boostedMargin,
      orderAmount
    };
  }, [data.margins]);

  const getMaxOrderAmount = () => {
    // Debug: Log the entire margins data structure
    addLog(`🔍 Debug margins data: ${JSON.stringify(data.margins)}`, 'info');
    
    addLog(`🔍 Extracted available margin: ₹${marginCalculations.availableMargin}`, 'info');
    
    // Check if margin data is available
    if (!marginCalculations.availableMargin || marginCalculations.availableMargin <= 0) {
      addLog('⚠️ No valid margin data available, using default order amount', 'warning');
      return 5000; // Default order amount when margin is not available
    }
    
    addLog(`💰 Margin calculation: Available=₹${marginCalculations.availableMargin}, Boosted(5X)=₹${marginCalculations.boostedMargin}, OrderAmount=₹${marginCalculations.orderAmount}`, 'info');
    
    return marginCalculations.orderAmount;
  }

  // Helper function to safely format numbers
  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return num.toFixed(2);
  }

  // Add log entry with enhanced logging
  const addLog = (message, type = 'info') => {
    const logEntry = {
      id: Date.now() + Math.random(),
      message,
      type,
      timestamp: new Date()
    }
    setIntradayLogs(prev => [logEntry, ...prev.slice(0, 99)]) // Keep last 100 logs
  }

  // --- FETCH STREAMING RECOMMENDATIONS ---
  const fetchStreamingRecommendations = async () => {
    if (!user?.kiteConnected) {
      addLog('❌ Kite not connected', 'error');
      return;
    }

    try {
      const intervalText = fetchInterval === 0.5 ? '30 seconds' : `${fetchInterval} minute(s)`;
      addLog(`📊 Fetching recommendations from last ${intervalText}...`, 'info');

      // Calculate the time window based on fetchInterval
      const now = new Date();
      const intervalMs = fetchInterval * 60 * 1000; // Convert minutes to milliseconds
      const startTime = new Date(now.getTime() - intervalMs);

      // Fetch recommendations from database within the time window
      const response = await recommendationsAPI.getLatestStreaming({
        startTime: startTime.toISOString(),
        endTime: now.toISOString()
      });

      if (response.data && response.data.success) {
        const latestData = response.data.data;

        // Update streaming recommendations with data from the time window
        setStreamingRecommendations({
          growth: latestData.growth || [],
          fall: latestData.fall || []
        });

        // Update last fetch time
        setLastFetchTime(now);
        setLastRecommendationFetch(now);

        addLog(`✅ Updated recommendations from last ${intervalText}: ${latestData.growth?.length || 0} growth, ${latestData.fall?.length || 0} fall`, 'success');

        // Automatically trigger Gemini analysis with latest data
        if (latestData.growth?.length > 0 && latestData.fall?.length > 0) {
          addLog('🤖 Triggering automatic Gemini analysis...', 'info');
          const result = await consultGeminiForOrder({ growth: latestData.growth || [], fall: latestData.fall || [] });
          if (!result) {
            addLog('⚠️ Gemini analysis was not completed - missing BUY or SELL recommendations', 'warning');
          }
        } else {
          addLog('⚠️ No complete recommendations available for Gemini analysis (need both BUY and SELL)', 'warning');
        }
      } else {
        addLog('❌ Failed to fetch streaming recommendations - invalid response', 'error');
      }
    } catch (error) {
      console.error('fetchStreamingRecommendations error:', error);
      addLog(`❌ Error fetching streaming recommendations: ${error.message}`, 'error');
      
      // Don't throw the error, just log it to prevent breaking the auto-refresh
      if (error.response) {
        addLog(`❌ Response status: ${error.response.status}, data: ${JSON.stringify(error.response.data)}`, 'error');
      }
    }
  };

  // --- START AUTOMATIC REFRESH ---
  const startAutoRefresh = () => {
    if (recommendationIntervalRef.current) {
      clearInterval(recommendationIntervalRef.current);
    }

    let lastFetchTimestamp = 0;

    // Fetch immediately
    const fetchAndSchedule = async () => {
      const now = Date.now();
      // Only fetch if enough time has passed since last fetch
      if (now - lastFetchTimestamp >= recommendationInterval * 1000) {
        await fetchStreamingRecommendations();
        lastFetchTimestamp = Date.now();
      }
    };

    fetchAndSchedule();

    recommendationIntervalRef.current = setInterval(fetchAndSchedule, recommendationInterval * 1000);

    addLog(`🔄 Auto-refresh started (${recommendationInterval} seconds)`, 'success');
  }

  // --- STOP AUTOMATIC REFRESH ---
  const stopAutoRefresh = () => {
    if (recommendationIntervalRef.current) {
      clearInterval(recommendationIntervalRef.current);
      recommendationIntervalRef.current = null;
    }
    addLog('⏹️ Auto-refresh stopped', 'warning');
  }

  // --- AUTOMATIC ORDER PLACEMENT ---
  const placeAutoBuyOrders = async () => {
    const availableMargin = Number(data.margins?.equity?.available || 0);
    if (!availableMargin || availableMargin <= 0) {
      addLog('❌ No margin available for auto orders', 'error');
      return;
    }

    // Check if we have Gemini analysis results
    if (!geminiResult?.bestBuy?.symbol) {
      addLog('⚠️ No Gemini BUY recommendation available for auto orders', 'warning');
      return;
    }

    // Ensure margin data is available
    await ensureMarginData();

    const maxOrderAmount = getMaxOrderAmount();
    
    addLog(`💰 Available margin: ₹${availableMargin.toFixed(2)}`, 'info');
    addLog(`📊 Max order amount per stock: ₹${maxOrderAmount.toFixed(2)}`, 'info');
    addLog(`🤖 Using Gemini recommendation: ${geminiResult.bestBuy.symbol}`, 'info');

    try {
      const symbol = geminiResult.bestBuy.symbol.replace('-EQ', '');
      const ltp = await fetchRobustLTP(symbol);
      
      if (!ltp) {
        addLog(`❌ No LTP available for ${symbol}`, 'error');
        return;
      }

      const quantity = calculateOrderQuantity(ltp, maxOrderAmount);
      
      addLog(`📊 Auto order calculation for ${symbol}: LTP=₹${ltp}, MaxAmount=₹${maxOrderAmount}, Quantity=${quantity}`, 'info');
      
      if (quantity > 0) {
        const orderParams = {
          tradingsymbol: `${symbol}-EQ`,
          exchange: 'NSE',
          transaction_type: 'BUY',
          order_type: 'MARKET',
          quantity: quantity,
          product: 'MIS',
          validity: 'DAY',
          reason: `Auto BUY (Gemini): ${geminiResult.bestBuy.symbol} - ${geminiResult.bestBuy.reason}`
        };

        await placeRobustOrder(orderParams);
        addLog(`✅ Auto BUY order placed for ${symbol}: ${quantity} shares @ ₹${ltp}`, 'success');
        setLastAutoOrderTime(new Date());
      } else {
        addLog(`⚠️ No auto BUY order placed for ${symbol} - insufficient quantity (${quantity})`, 'warning');
      }
    } catch (error) {
      addLog(`❌ Failed to place auto BUY order: ${error.message}`, 'error');
    }
  }

  // --- START AUTOMATIC ORDER PLACEMENT ---
  const startAutoOrders = () => {
    if (autoOrderIntervalRef.current) {
      clearInterval(autoOrderIntervalRef.current);
    }
    
    // Place orders immediately
    placeAutoBuyOrders();
    
    // Set up interval (every 2 minutes)
    autoOrderIntervalRef.current = setInterval(() => {
      placeAutoBuyOrders();
    }, 2 * 60 * 1000); // 2 minutes
    
    addLog('🤖 Auto order placement started (every 2 minutes)', 'success');
  }

  // --- STOP AUTOMATIC ORDER PLACEMENT ---
  const stopAutoOrders = () => {
    if (autoOrderIntervalRef.current) {
      clearInterval(autoOrderIntervalRef.current);
      autoOrderIntervalRef.current = null;
    }
    addLog('⏹️ Auto order placement stopped', 'warning');
  }

  // --- CONSULT GEMINI FOR ORDER PLACEMENT ---
  const consultGeminiForOrder = async (recommendations) => {
    if (!autoManagementActive) {
      toast.warning('Auto-management is paused. Start Auto to enable Gemini analysis.');
      addLog('⚠️ Gemini analysis skipped: Auto-management is paused.', 'warning');
      return;
    }

    if (!recommendations || (!recommendations.growth?.length && !recommendations.fall?.length)) {
      addLog('⚠️ No recommendations available for Gemini analysis', 'warning');
      return null;
    }

    setGeminiLoading(true);
    addLog('🤖 Consulting Gemini AI for order recommendations...', 'info');

    try {
      // Filter valid recommendations
      const validGrowth = (recommendations.growth || []).filter(rec => 
        rec && rec.symbol && rec.symbol !== 'N/A' && rec.symbol !== 'undefined' && 
        rec.symbol.trim() !== '' && rec.ltp && rec.ltp > 0 && rec.close && rec.close > 0
      );

      const validFall = (recommendations.fall || []).filter(rec => 
        rec && rec.symbol && rec.symbol !== 'N/A' && rec.symbol !== 'undefined' && 
        rec.symbol.trim() !== '' && rec.ltp && rec.ltp > 0 && rec.close && rec.close > 0
      );

      if (validGrowth.length === 0 && validFall.length === 0) {
        addLog('❌ No valid recommendations found for Gemini analysis', 'error');
        setGeminiResult({ error: 'No valid stock data available for analysis' });
        setGeminiLoading(false);
        return null;
      }

      // Only proceed if we have both BUY and SELL recommendations
      if (validGrowth.length === 0 || validFall.length === 0) {
        addLog('⚠️ Gemini analysis requires both BUY and SELL recommendations. Current: BUY=' + validGrowth.length + ', SELL=' + validFall.length, 'warning');
        setGeminiResult({ error: 'Both BUY and SELL recommendations are required for analysis' });
        setGeminiLoading(false);
        return null;
      }

      addLog(`📊 Sending ${validGrowth.length} growth and ${validFall.length} fall recommendations to Gemini`, 'info');

      const response = await recommendationsAPI.getGeminiFinalRecommendation({
        buys: validGrowth,
        sells: validFall
      });

      setGeminiResult(response.data);
      setGeminiLastRun(new Date());

      if (response.data.bestBuy && response.data.bestSell) {
        addLog('✅ Gemini analysis completed successfully', 'success');
        addLog(`🎯 Best BUY: ${response.data.bestBuy.symbol} - ${response.data.bestBuy.reason}`, 'success');
        addLog(`🎯 Best SELL: ${response.data.bestSell.symbol} - ${response.data.bestSell.reason}`, 'success');
      } else {
        addLog('⚠️ Gemini analysis completed but no actionable recommendations', 'warning');
      }

      setGeminiLoading(false);
      return response.data;
    } catch (error) {
      console.error('Gemini analysis error:', error);
      setGeminiResult({ error: error.message });
      addLog(`❌ Gemini analysis failed: ${error.message}`, 'error');
      setGeminiLoading(false);
      return null;
    }
  };

  // --- PLACE ORDERS BASED ON GEMINI RECOMMENDATIONS ---
  const placeOrdersFromGemini = async (geminiResult) => {
    if (!geminiResult || geminiResult.error) {
      addLog('⚠️ No valid Gemini recommendations for order placement', 'warning');
      return;
    }

    try {
      const ordersToPlace = [];

      // Process best buy recommendation
      if (geminiResult.bestBuy && geminiResult.bestBuy.symbol) {
        const symbol = geminiResult.bestBuy.symbol.replace('-EQ', '');
        const currentTime = Date.now();
        const lastOrderTime = lastGeminiBuyRef.current[symbol] || 0;

        // Check cooldown
        if (currentTime - lastOrderTime < GEMINI_AUTOBUY_INTERVAL) {
          addLog(`⏳ Skipping ${symbol} - cooldown active`, 'warning');
        } else {
          // Get margin and calculate quantity
          const maxOrderAmount = getMaxOrderAmount();
          
          // Get current LTP
          const ltp = await fetchRobustLTP(symbol);
          if (!ltp) {
            addLog(`❌ No LTP available for ${symbol}`, 'error');
            return;
          }

          const quantity = calculateOrderQuantity(ltp, maxOrderAmount);
          if (quantity > 0) {
            ordersToPlace.push({
              tradingsymbol: `${symbol}-EQ`,
              exchange: 'NSE',
              transaction_type: 'BUY',
              order_type: 'MARKET',
              quantity: quantity,
              product: 'MIS',
              validity: 'DAY',
              reason: `Gemini AI BUY: ${geminiResult.bestBuy.reason}`
            });
          }
        }
      }

      // Process best sell recommendation
      if (geminiResult.bestSell && geminiResult.bestSell.symbol) {
        const symbol = geminiResult.bestSell.symbol.replace('-EQ', '');
        const currentTime = Date.now();
        const lastOrderTime = lastGeminiBuyRef.current[symbol] || 0;

        // Check cooldown
        if (currentTime - lastOrderTime < GEMINI_AUTOBUY_INTERVAL) {
          addLog(`⏳ Skipping ${symbol} - cooldown active`, 'warning');
        } else {
          // Get margin and calculate quantity
          const maxOrderAmount = getMaxOrderAmount();
          
          // Get current LTP
          const ltp = await fetchRobustLTP(symbol);
          if (!ltp) {
            addLog(`❌ No LTP available for ${symbol}`, 'error');
            return;
          }

          const quantity = calculateOrderQuantity(ltp, maxOrderAmount);
          if (quantity > 0) {
            ordersToPlace.push({
              tradingsymbol: `${symbol}-EQ`,
              exchange: 'NSE',
              transaction_type: 'SELL',
              order_type: 'MARKET',
              quantity: quantity,
              product: 'MIS',
              validity: 'DAY',
              reason: `Gemini AI SELL: ${geminiResult.bestSell.reason}`
            });
          }
        }
      }

      // Place orders
      for (const orderParams of ordersToPlace) {
        try {
          addLog(`📈 Placing ${orderParams.transaction_type} order for ${orderParams.tradingsymbol} (${orderParams.reason})`, 'info');
          await placeRobustOrder(orderParams);
          
          // Update cooldown
          const symbol = orderParams.tradingsymbol.replace('-EQ', '');
          lastGeminiBuyRef.current[symbol] = Date.now();
          
          addLog(`✅ Order placed successfully for ${orderParams.tradingsymbol}`, 'success');
        } catch (error) {
          addLog(`❌ Failed to place order for ${orderParams.tradingsymbol}: ${error.message}`, 'error');
        }
      }

    } catch (error) {
      addLog(`❌ Error in placeOrdersFromGemini: ${error.message}`, 'error');
    }
  };

  // --- START RECOMMENDATION FETCHING ---
  const startRecommendationFetching = () => {
    if (recommendationIntervalRef.current) return;
    
    addLog('📊 Started recommendation fetching from database', 'success');
    recommendationIntervalRef.current = setInterval(async () => {
      if (user?.kiteConnected) {
        const recommendations = await fetchStreamingRecommendations();
        if (recommendations && (recommendations.growth.length > 0 || recommendations.fall.length > 0)) {
          const geminiResult = await consultGeminiForOrder(recommendations);
          if (geminiResult && !geminiResult.error) {
            await placeOrdersFromGemini(geminiResult);
          }
        }
      }
    }, recommendationInterval * 1000);
  }

  // --- STOP RECOMMENDATION FETCHING ---
  const stopRecommendationFetching = () => {
    if (recommendationIntervalRef.current) {
      clearInterval(recommendationIntervalRef.current);
      recommendationIntervalRef.current = null;
      addLog('⏹️ Stopped recommendation fetching', 'warning');
    }
  }

  // --- ROBUST VALIDATION FUNCTIONS ---
  const validateMISOrder = (orderParams) => {
    const errors = [];
    
    // Ensure product is MIS
    if (orderParams.product !== 'MIS') {
      errors.push('Only MIS orders are allowed for intraday trading');
    }
    
    // Validate required fields
    if (!orderParams.tradingsymbol) {
      errors.push('Trading symbol is required');
    }
    
    if (!orderParams.quantity || orderParams.quantity <= 0) {
      errors.push('Valid quantity is required');
    }
    
    if (!orderParams.transaction_type || !['BUY', 'SELL'].includes(orderParams.transaction_type)) {
      errors.push('Valid transaction type (BUY/SELL) is required');
    }
    
    // Check for duplicate orders
    const symbol = orderParams.tradingsymbol;
    const now = Date.now();
    if (orderCooldownRef.current[symbol] && now - orderCooldownRef.current[symbol] < MIN_ORDER_INTERVAL) {
      errors.push(`Order for ${symbol} placed too recently. Please wait ${Math.ceil((MIN_ORDER_INTERVAL - (now - orderCooldownRef.current[symbol])) / 1000)} seconds`);
    }
    
    // Check concurrent orders limit
    const currentOrders = data.orders.filter(order => 
      ['OPEN', 'TRIGGER PENDING', 'PENDING', 'NEW'].includes(order.status)
    );
    if (currentOrders.length >= MAX_CONCURRENT_ORDERS) {
      errors.push(`Maximum ${MAX_CONCURRENT_ORDERS} concurrent orders allowed`);
    }
    
    return errors;
  };

  const isPositionActive = (position) => {
    // Only consider positions that are not squared off
    return position.quantity !== 0 && 
           String(position.product || '').toUpperCase() === 'MIS' &&
           !['SQUARED OFF', 'CANCELLED'].includes(String(position.status || '').toUpperCase());
  };

  const isOrderActive = (order) => {
    // Only consider orders that are active and not completed
    return ['OPEN', 'TRIGGER PENDING', 'PENDING', 'NEW'].includes(String(order.status || '').toUpperCase()) &&
           String(order.product || '').toUpperCase() === 'MIS';
  };

  // --- ROBUST ORDER PLACEMENT ---
  const placeRobustOrder = async (orderParams) => {
    if (isProcessingOrder.current) {
      addLog('⚠️ Another order is being processed. Please wait.', 'warning');
      return false;
    }

    // Validate order parameters
    const validationErrors = validateMISOrder(orderParams);
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => addLog(`❌ Validation Error: ${error}`, 'error'));
      setValidationErrors(validationErrors);
      return false;
    }

    setIsProcessingOrder(true);
    const symbol = orderParams.tradingsymbol;
    
    try {
      addLog(`🔄 Placing order for ${symbol}: ${orderParams.transaction_type} ${orderParams.quantity} @ ₹${orderParams.price || 'Market'}`);
      
      // Place the order
      const response = await portfolioAPI.placeOrder(orderParams);
      
      if (response.data && response.data.data && response.data.data.order_id) {
        addLog(`✅ Order placed successfully for ${symbol} (Order ID: ${response.data.data.order_id})`, 'success');
        toast.success(`Order placed for ${symbol}`);
        
        // Update tracking
        orderCooldownRef.current[symbol] = Date.now();
        setOrderHistory(prev => new Set([...prev, response.data.data.order_id]));
        
        // Refresh data
        await fetchIntradayData();
        return true;
      } else {
        throw new Error('Invalid response from order placement');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      addLog(`❌ Failed to place order for ${symbol}: ${errorMessage}`, 'error');
      toast.error(`Order failed: ${errorMessage}`);
      return false;
    } finally {
      setIsProcessingOrder(false);
    }
  };

  // --- ROBUST AUTO MANAGEMENT ---
  const performRobustAutoManagement = async () => {
    if (!autoManagementActive) return;
    try {
      addLog('🤖 Starting robust auto-management cycle...')
      // Always fetch fresh data at the start of the cycle
      await fetchIntradayData()
      // Get only active MIS positions and orders
      let activeMISPositions = data.positions.filter(isPositionActive);
      let activeMISOrders = data.orders.filter(isOrderActive);
      addLog(`📊 Found ${activeMISPositions.length} active MIS positions and ${activeMISOrders.length} active MIS orders`)
      // Auto-cancel old orders
      await autoCancelOldOrders(activeMISOrders)
      // Always refresh after cancels
      await fetchIntradayData();
      activeMISPositions = data.positions.filter(isPositionActive);
      activeMISOrders = data.orders.filter(isOrderActive);
      // Auto-place stop loss orders
      await autoPlaceStopLossOrders(activeMISPositions, activeMISOrders)
      // Always refresh after SL
      await fetchIntradayData();
      activeMISPositions = data.positions.filter(isPositionActive);
      activeMISOrders = data.orders.filter(isOrderActive);
      // Auto-exit on profit for MIS
      await autoExitOnProfitMIS(activeMISPositions, activeMISOrders)
      addLog('✅ Robust auto-management cycle completed', 'success')
    } catch (error) {
      addLog(`❌ Auto-management error: ${error.message}`, 'error')
    }
  }

  const autoCancelOldOrders = async (activeOrders) => {
    const cutoffTime = new Date(Date.now() - AUTO_CANCEL_MINUTES * 60 * 1000)
    
    const oldOrders = activeOrders.filter(order => {
      const orderTime = new Date(order.order_timestamp)
      return orderTime < cutoffTime
    })

    for (const order of oldOrders) {
      try {
        addLog(`⏰ Auto-cancelling old order ${order.order_id} for ${order.tradingsymbol}`)
        await portfolioAPI.cancelOrder(order.order_id)
        addLog(`✅ Auto-cancelled order ${order.order_id}`, 'success')
      } catch (error) {
        addLog(`❌ Failed to auto-cancel order ${order.order_id}: ${error.message}`, 'error')
      }
    }
  }

  // --- ROBUST SL PLACEMENT ---
  // Manual SL placement for all open/executed positions without SL
  const handleManualPlaceSLOrders = async () => {
    addLog('🛡️ Manual SL placement triggered for all open/executed positions...', 'info');
    // Refresh latest data
    await fetchIntradayData();
    const activePositions = data.positions.filter(isPositionActive);
    const activeOrders = data.orders.filter(isOrderActive);
    let placedCount = 0;
    for (const position of activePositions) {
      // Skip if position is closed, squared off, or cancelled
      if (!isPositionActive(position)) continue;
      // Skip if SL order already exists for this position
      const existingSL = activeOrders.find(order =>
        order.tradingsymbol === position.tradingsymbol &&
        String(order.order_type || '').toUpperCase() === 'SL' &&
        ['OPEN', 'TRIGGER PENDING', 'PENDING', 'NEW'].includes(String(order.status || '').toUpperCase())
      );
      if (existingSL) continue;
      // Place SL order
      try {
        const cleanSymbol = position.tradingsymbol.replace('-EQ', '');
        let currentLTP = await fetchRobustLTP(cleanSymbol);
        if (!currentLTP) {
          addLog(`❌ No LTP data for ${cleanSymbol}. Skipping manual SL.`, 'error');
          continue;
        }
        const TICK_SIZE = 0.05;
        let slRaw = position.quantity > 0
          ? currentLTP * (1 - STOP_LOSS_PERCENTAGE / 100)
          : currentLTP * (1 + STOP_LOSS_PERCENTAGE / 100);
        let slPrice = position.quantity > 0
          ? Math.floor(slRaw / TICK_SIZE) * TICK_SIZE
          : Math.ceil(slRaw / TICK_SIZE) * TICK_SIZE;
        slPrice = Number(slPrice.toFixed(2));
        const slOrderParams = {
          tradingsymbol: position.tradingsymbol,
          exchange: position.exchange || 'NSE',
          transaction_type: position.quantity > 0 ? 'SELL' : 'BUY',
          order_type: 'SL',
          quantity: Math.abs(position.quantity),
          trigger_price: slPrice,
          price: slPrice,
          product: 'MIS',
          validity: 'DAY'
        };
        addLog(`🛡️ Placing manual SL for ${position.tradingsymbol}: ${slOrderParams.transaction_type} SL @ ₹${slPrice}`);
        await placeRobustOrder(slOrderParams);
        placedCount++;
      } catch (error) {
        addLog(`❌ Manual SL failed for ${position.tradingsymbol}: ${error.message}`, 'error');
      }
    }
    addLog(`🛡️ Manual SL placement complete. SL orders placed: ${placedCount}`, 'success');
    await fetchIntradayData();
  };
  const autoPlaceStopLossOrders = async (activePositions, activeOrders) => {
    for (const position of activePositions) {
      if (!isPositionActive(position)) {
        addLog(`⏭️ Skipping SL for ${position.tradingsymbol}: position is not active (quantity=0 or squared off)`);
        continue;
      }
      // Check if SL order already exists (only SL Limit)
      const existingSL = activeOrders.find(order => 
        order.tradingsymbol === position.tradingsymbol &&
        String(order.order_type || '').toUpperCase() === 'SL' &&
        ['OPEN', 'TRIGGER PENDING', 'PENDING', 'NEW'].includes(String(order.status || '').toUpperCase())
      );
      if (existingSL) {
        addLog(`✅ SL order already exists for ${position.tradingsymbol} (Order ID: ${existingSL.order_id})`);
        continue;
      }
      // Check for any open non-SL orders for this symbol
      const openOrderExists = activeOrders.some(order =>
        order.tradingsymbol === position.tradingsymbol &&
        String(order.order_type || '').toUpperCase() !== 'SL' &&
        ['OPEN', 'TRIGGER PENDING', 'PENDING', 'NEW'].includes(String(order.status || '').toUpperCase())
      );
      if (openOrderExists) {
        addLog(`⏭️ Skipping SL for ${position.tradingsymbol}: open non-SL order exists`);
        continue;
      }
      try {
        // Fetch current LTP with fallback
        const cleanSymbol = position.tradingsymbol.replace('-EQ', '');
        let currentLTP = await fetchRobustLTP(cleanSymbol);
        if (!currentLTP) {
          addLog(`❌ No LTP data available for ${cleanSymbol}. Skipping SL order.`, 'error');
          continue;
        }
        // Calculate SL price: 0.5% away from LTP, rounded to nearest tick (0.05)
        const TICK_SIZE = 0.05;
        let slRaw = position.quantity > 0
          ? currentLTP * (1 - STOP_LOSS_PERCENTAGE / 100)
          : currentLTP * (1 + STOP_LOSS_PERCENTAGE / 100);
        // Always round to nearest tick DOWN for long, UP for short
        let slPrice = position.quantity > 0
          ? Math.floor(slRaw / TICK_SIZE) * TICK_SIZE
          : Math.ceil(slRaw / TICK_SIZE) * TICK_SIZE;
        slPrice = Number(slPrice.toFixed(2));
        // Place SL order only if position is still open
        if (!isPositionActive(position)) {
          addLog(`⏭️ Skipping SL for ${position.tradingsymbol}: position is no longer active after LTP fetch.`);
          continue;
        }
        const slOrderParams = {
          tradingsymbol: position.tradingsymbol,
          exchange: position.exchange || 'NSE',
          transaction_type: position.quantity > 0 ? 'SELL' : 'BUY',
          order_type: 'SL',
          quantity: Math.abs(position.quantity),
          trigger_price: slPrice,
          price: slPrice, // For SL Limit, price should be same as trigger_price
          product: 'MIS', // Ensure MIS
          validity: 'DAY'
        };
        addLog(`🛡️ Placing SL order for ${position.tradingsymbol}: ${slOrderParams.transaction_type} SL @ ₹${slPrice}`);
        await placeRobustOrder(slOrderParams);
        // Always refresh after placing an order
        await fetchIntradayData();
      } catch (error) {
        addLog(`❌ Failed to place SL for ${position.tradingsymbol}: ${error.message}`, 'error');
      }
    }
  }

  // --- NEW: Robust Auto-Exit on 0.5% Profit for Both Long and Short ---
  const PROFIT_EXIT_THRESHOLD = 0.005; // 0.5%
  const PRICE_BUFFER = 0.01; // 1 paisa buffer for better fill

  const autoExitOnProfitMIS = async (activePositions, activeOrders) => {
    for (const position of activePositions) {
      if (!isPositionActive(position)) {
        addLog(`⏭️ Skipping profit exit for ${position.tradingsymbol}: position is not active (quantity=0 or squared off)`);
        continue;
      }
      const isLong = position.quantity > 0;
      const entry = position.average_price;
      const symbol = position.tradingsymbol;
      const cleanSymbol = symbol.replace('-EQ', '');
      // 1. Check for any open exit/SL order for this position
      const hasOpenExitOrSL = activeOrders.some(order =>
        order.tradingsymbol === symbol &&
        (
          // Any open SELL/BUY order (exit)
          ['SELL', 'BUY'].includes(String(order.transaction_type || '').toUpperCase()) &&
          ['OPEN', 'TRIGGER PENDING', 'PENDING', 'NEW'].includes(String(order.status || '').toUpperCase())
        )
      );
      if (hasOpenExitOrSL) {
        addLog(`⏭️ Skipping profit exit for ${symbol}: open exit/SL order exists`);
        continue;
      }
      // 2. Get the freshest LTP
      let ltp = await fetchRobustLTP(cleanSymbol);
      if (!ltp) {
        addLog(`❌ No LTP data for ${symbol}. Skipping profit exit.`, 'error');
        continue;
      }
      // 3. Re-fetch the position to ensure it is still open and get the latest quantity
      await fetchIntradayData();
      const latestPosition = data.positions.find(p => p.tradingsymbol === symbol && isPositionActive(p));
      if (!latestPosition) {
        addLog(`⏭️ Skipping profit exit for ${symbol}: position is no longer active after LTP fetch.`);
        continue;
      }
      // 4. Check if profit target is still reached with the new LTP
      const profitTargetReached = (
        (isLong && ltp >= entry * (1 + PROFIT_EXIT_THRESHOLD)) ||
        (!isLong && ltp <= entry * (1 - PROFIT_EXIT_THRESHOLD))
      );
      if (!profitTargetReached) {
        addLog(`⏭️ Skipping profit exit for ${symbol}: LTP no longer meets profit threshold after re-check.`);
        continue;
      }
      // 5. Prepare robust exit order (LIMIT at LTP +/- buffer)
      let exitPrice = isLong ? ltp - PRICE_BUFFER : ltp + PRICE_BUFFER;
      exitPrice = Math.round(exitPrice * 100) / 100;
      const exitOrderParams = {
        tradingsymbol: symbol,
        exchange: latestPosition.exchange || 'NSE',
        transaction_type: isLong ? 'SELL' : 'BUY',
        order_type: 'LIMIT',
        quantity: Math.abs(latestPosition.quantity),
        price: exitPrice,
        product: 'MIS',
        validity: 'DAY'
      };
      addLog(`💸 Profit exit: Placing ${exitOrderParams.transaction_type} LIMIT at ₹${exitPrice} for ${symbol} (Entry: ₹${entry}, LTP: ₹${ltp})`);
      await placeRobustOrder(exitOrderParams);
      // Always refresh after placing an order
      await fetchIntradayData();
    }
  }

  // --- ROBUST LTP FETCHING WITH FALLBACK ---
  const fetchRobustLTP = async (symbol) => {
    // Validate symbol before making API calls
    if (!symbol || symbol === 'N/A' || symbol === 'undefined' || symbol.trim() === '') {
      addLog(`❌ Invalid symbol provided: ${symbol}`, 'error');
      return null;
    }
    
    let currentLTP = null;
    let ltpSource = 'none';
    
    // 1. Try stockDataAPI.getOHLC
    try {
      const response = await stockDataAPI.getOHLC(symbol);
      if (response.data.status && response.data.data.fetched.length > 0) {
        currentLTP = response.data.data.fetched[0].ltp;
        ltpSource = 'OHLC';
      }
    } catch (err) {
      addLog(`❌ OHLC LTP fetch failed for ${symbol}: ${err.message}`, 'error');
    }
    
    // 2. Fallback: kiteAPI.getQuote
    if (!currentLTP) {
      try {
        const kiteRes = await kiteAPI.getQuote(symbol);
        const kiteQuote = kiteRes.data.data?.[symbol] || kiteRes.data.data || {};
        if (kiteQuote.last_price || kiteQuote.ltp) {
          currentLTP = kiteQuote.last_price || kiteQuote.ltp;
          ltpSource = 'kiteAPI';
        }
      } catch (err) {
        addLog(`❌ kiteAPI LTP fetch failed for ${symbol}: ${err.message}`, 'error');
      }
    }
    
    // 3. Fallback: watchlistAPI.getQuotes
    if (!currentLTP && selectedWatchlist && selectedWatchlist._id) {
      try {
        const quotesRes = await watchlistAPI.getQuotes(selectedWatchlist._id);
        const quotes = quotesRes.data.data || {};
        const quote = quotes[symbol] || Object.values(quotes).find(q => (q.tradingsymbol || q.symbol) === symbol);
        if (quote && (quote.last_price || quote.ltp)) {
          currentLTP = quote.last_price || quote.ltp;
          ltpSource = 'watchlistAPI';
        }
      } catch (err) {
        addLog(`❌ watchlistAPI LTP fetch failed for ${symbol}: ${err.message}`, 'error');
      }
    }
    
    if (currentLTP) {
      addLog(`📈 LTP for ${symbol} (${ltpSource}): ₹${currentLTP}`);
    } else {
      addLog(`❌ No LTP available for ${symbol}`, 'error');
    }
    
    return currentLTP;
  };

  // --- ENHANCED DATA FETCHING ---
  const fetchIntradayData = async () => {
    if (isCurrentlyFetching.current) return
    
    isCurrentlyFetching.current = true
    
    try {
      addLog('🔄 Fetching intraday data...')
      const [marginsRes, positionsRes, ordersRes] = await Promise.allSettled([
        portfolioAPI.getMargins(),
        portfolioAPI.getPositions(),
        portfolioAPI.getOrders()
      ])

      const margins = marginsRes.status === 'fulfilled' ? marginsRes.value.data.data : null
      const positions = positionsRes.status === 'fulfilled' ? positionsRes.value.data.data?.net || [] : []
      const orders = ordersRes.status === 'fulfilled' ? ordersRes.value.data.data || [] : []

      // Filter only active MIS positions and orders
      const activeMISPositions = positions.filter(isPositionActive);
      const activeMISOrders = orders.filter(isOrderActive);

      // Calculate day's P&L from active positions only
      const dayPnL = activeMISPositions.reduce((sum, position) => sum + (position.pnl || 0), 0)

      setData({
        margins,
        positions: activeMISPositions,
        orders: activeMISOrders,
        dayPnL
      })

      addLog(`📊 Data refreshed: ${activeMISPositions.length} active MIS positions, ${activeMISOrders.length} active MIS orders`)
      
      // --- AUTO SL LOGIC: If any active MIS position is missing a matching SL order, call SL endpoint ---
      const needsSL = activeMISPositions.some(position => {
        return !activeMISOrders.some(order =>
          String(order.order_type).toUpperCase() === 'SL' &&
          order.tradingsymbol === position.tradingsymbol &&
          Math.abs(order.quantity) === Math.abs(position.quantity) &&
          ((order.transaction_type === 'SELL' && position.quantity > 0) ||
           (order.transaction_type === 'BUY' && position.quantity < 0)) &&
          // Only consider active SL orders
          ['OPEN', 'TRIGGER PENDING', 'PENDING', 'NEW'].includes(String(order.status || '').toUpperCase())
        );
      });
      if (activeMISPositions.length > 0 && needsSL) {
        addLog('🛡️ At least one open MIS position is missing a matching SL order. Triggering SL order placement...', 'warning');
        // Call backend endpoint to check and place SL orders
        try {
          addLog(`🔐 User token available: ${!!user?.token}`, 'info');
          addLog(`🔗 Kite connected: ${!!user?.kiteConnected}`, 'info');
          
          if (!user?.kiteConnected) {
            addLog('⚠️ Kite not connected. Cannot place SL orders.', 'warning');
            return;
          }
          
          const response = await slOrdersAPI.checkAndPlace();
          const data = response.data;
          
          if (data && data.results) {
            addLog(`🛡️ SL order check: ${data.results.map(r => r.symbol + ': ' + r.status).join(', ')}`);
          } else if (data && data.message) {
            addLog(`🛡️ SL order check: ${data.message}`);
          }
        } catch (err) {
          addLog(`❌ Failed to trigger SL order check: ${err.message}`, 'error');
          if (err.response) {
            addLog(`❌ Response status: ${err.response.status}, data: ${JSON.stringify(err.response.data)}`, 'error');
          }
        }
      }

      return {
        margins,
        positions: activeMISPositions,
        orders: activeMISOrders,
        dayPnL
      }
    } catch (error) {
      console.error('Intraday data fetch error:', error)
      addLog(`❌ Failed to fetch data: ${error.message}`, 'error')
      toast.error('Failed to fetch intraday data')
      return null
    } finally {
      isCurrentlyFetching.current = false
      setRefreshing(false)
      if (loading) setLoading(false)
    }
  }

  // --- ENHANCED ORDER MANAGEMENT ---
  const exitPosition = async (position) => {
    if (!isPositionActive(position)) {
      addLog(`⚠️ Cannot exit position ${position.tradingsymbol}: Position is not active`, 'warning');
      return;
    }

    setExitingPosition(position.tradingsymbol)
    addLog(`🚪 Initiating exit for position ${position.tradingsymbol}`)
    
    try {
      const cleanSymbol = position.tradingsymbol.replace('-EQ', '')
      const currentLTP = await fetchRobustLTP(cleanSymbol);
      
      if (!currentLTP) {
        throw new Error('Unable to fetch current price')
      }

      // Calculate exit price based on position type
      let exitPrice;
      const isLongPosition = position.quantity > 0
      
      if (isLongPosition) {
        exitPrice = currentLTP * (1 - 0.0005) // 0.05% below LTP
      } else {
        exitPrice = currentLTP * (1 + 0.0005) // 0.05% above LTP
      }

      exitPrice = Math.round(exitPrice * 100) / 100

      const orderParams = {
        tradingsymbol: position.tradingsymbol,
        exchange: position.exchange || 'NSE',
        transaction_type: isLongPosition ? 'SELL' : 'BUY',
        order_type: 'LIMIT',
        quantity: Math.abs(position.quantity),
        price: exitPrice,
        product: 'MIS', // Ensure MIS
        validity: 'DAY'
      }

      const success = await placeRobustOrder(orderParams);
      if (success) {
        addLog(`✅ Exit order placed successfully for ${position.tradingsymbol}`, 'success')
        toast.success(`Exit order placed for ${position.tradingsymbol} at ₹${exitPrice}`)
      }
      
    } catch (error) {
      console.error('Exit position error:', error)
      addLog(`❌ Exit order failed for ${position.tradingsymbol}: ${error.message}`, 'error')
      toast.error(`Failed to exit position: ${error.message}`)
    } finally {
      setExitingPosition(null)
    }
  }

  const cancelOrder = async (order) => {
    if (!isOrderActive(order)) {
      addLog(`⚠️ Cannot cancel order ${order.order_id}: Order is not active`, 'warning');
      return;
    }

    setCancellingOrder(order.order_id)
    addLog(`🚫 Cancelling order ${order.order_id} for ${order.tradingsymbol}`)
    
    try {
      await portfolioAPI.cancelOrder(order.order_id)
      
      addLog(`✅ Order ${order.order_id} cancelled successfully`, 'success')
      toast.success(`Order ${order.order_id} cancelled successfully`)
      fetchIntradayData() // Refresh data
      
    } catch (error) {
      console.error('Cancel order error:', error)
      addLog(`❌ Failed to cancel order ${order.order_id}: ${error.message}`, 'error')
      toast.error(`Failed to cancel order: ${error.message}`)
    } finally {
      setCancellingOrder(null)
    }
  }

  // --- AUTO MANAGEMENT CONTROLS ---
  const startAutoManagement = () => {
    if (autoManagementRef.current) return;
    setAutoManagementActive(true);
    addLog('🤖 Started robust auto-management (MIS orders only)', 'success');
    if (typeof startStreaming === 'function' && !isStreaming) {
      addLog('📡 Triggering Start Streaming as part of Start Auto');
      startStreaming();
    }
    autoManagementRef.current = setInterval(() => {
      if (!autoManagementActive) return;
      if (user?.kiteConnected) {
        performRobustAutoManagement();
      }
    }, 60000); // Check every minute
    startAutoOrders();
    startAutoRefresh();
    startRecommendationFetching();
  }

  const stopAutoManagement = () => {
    if (autoManagementRef.current) {
      clearInterval(autoManagementRef.current);
      autoManagementRef.current = null;
    }
    setAutoManagementActive(false);
    addLog('🛑 Stopped auto-management', 'warning');
    stopAutoOrders();
    stopAutoRefresh();
    stopRecommendationFetching();
  }

  // --- MANUAL ORDER PLACEMENT ---
  const placeManualBuyOrders = async () => {
    if (!geminiResult?.bestBuy?.symbol) {
      toast.error('No BUY recommendation available from Gemini analysis');
      return;
    }

    addLog('📈 Placing manual BUY order from Gemini analysis...', 'info');

    try {
      // Ensure margin data is available
      await ensureMarginData();

      // Ensure symbol is uppercase and ends with '-EQ' for API
      let symbol = geminiResult.bestBuy.symbol.toUpperCase();
      if (!symbol.endsWith('-EQ')) {
        symbol = symbol + '-EQ';
      }
      const ltp = await fetchRobustLTP(symbol.replace('-EQ', ''));

      if (!ltp) {
        addLog(`❌ No LTP available for ${symbol}`, 'error');
        toast.error(`No LTP available for ${symbol}`);
        return;
      }

      // Calculate quantity based on available margin
      const maxOrderAmount = getMaxOrderAmount();
      const quantity = calculateOrderQuantity(ltp, maxOrderAmount);

      addLog(`📊 Order calculation for ${symbol}: LTP=₹${ltp}, MaxAmount=₹${maxOrderAmount}, Quantity=${quantity}`, 'info');

      if (quantity > 0) {
        const orderParams = {
          tradingsymbol: symbol,
          exchange: 'NSE',
          transaction_type: 'BUY',
          order_type: 'MARKET',
          quantity: quantity,
          product: 'MIS',
          validity: 'DAY',
          reason: `Manual BUY (Gemini): ${geminiResult.bestBuy.symbol} - ${geminiResult.bestBuy.reason}`
        };

        await placeRobustOrder(orderParams);
        addLog(`✅ Manual BUY order placed for ${symbol}: ${quantity} shares @ ₹${ltp}`, 'success');
        toast.success(`BUY order placed for ${symbol}`);
      } else {
        addLog(`⚠️ No BUY order placed for ${symbol} - insufficient quantity (${quantity})`, 'warning');
        toast.error('Insufficient quantity for BUY order');
      }
    } catch (error) {
      addLog(`❌ Failed to place manual BUY order: ${error.message}`, 'error');
      toast.error('Failed to place BUY order');
    }
  }

  const placeManualSellOrders = async () => {
    if (!geminiResult?.bestSell?.symbol) {
      toast.error('No SELL recommendation available from Gemini analysis');
      return;
    }

    addLog('📉 Placing manual SELL order from Gemini analysis...', 'info');

    try {
      // Ensure margin data is available
      await ensureMarginData();

      // Ensure symbol is uppercase and ends with '-EQ' for API
      let symbol = geminiResult.bestSell.symbol.toUpperCase();
      if (!symbol.endsWith('-EQ')) {
        symbol = symbol + '-EQ';
      }
      const ltp = await fetchRobustLTP(symbol.replace('-EQ', ''));

      if (!ltp) {
        addLog(`❌ No LTP available for ${symbol}`, 'error');
        toast.error(`No LTP available for ${symbol}`);
        return;
      }

      // Calculate quantity based on available margin
      const maxOrderAmount = getMaxOrderAmount();
      const quantity = calculateOrderQuantity(ltp, maxOrderAmount);

      addLog(`📊 Order calculation for ${symbol}: LTP=₹${ltp}, MaxAmount=₹${maxOrderAmount}, Quantity=${quantity}`, 'info');

      if (quantity > 0) {
        const orderParams = {
          tradingsymbol: symbol,
          exchange: 'NSE',
          transaction_type: 'SELL',
          order_type: 'MARKET',
          quantity: quantity,
          product: 'MIS',
          validity: 'DAY',
          reason: `Manual SELL (Gemini): ${geminiResult.bestSell.symbol} - ${geminiResult.bestSell.reason}`
        };

        await placeRobustOrder(orderParams);
        addLog(`✅ Manual SELL order placed for ${symbol}: ${quantity} shares @ ₹${ltp}`, 'success');
        toast.success(`SELL order placed for ${symbol}`);
      } else {
        addLog(`⚠️ No SELL order placed for ${symbol} - insufficient quantity (${quantity})`, 'warning');
        toast.error('Insufficient quantity for SELL order');
      }
    } catch (error) {
      addLog(`❌ Failed to place manual SELL order: ${error.message}`, 'error');
      toast.error('Failed to place SELL order');
    }
  }

  // --- INITIALIZATION ---
  useEffect(() => {
    if (user?.kiteConnected) {
      setLoading(true)
      fetchIntradayData()
      startAutoManagement()
      startAutoRefresh()
      startRecommendationFetching() // Start fetching recommendations
      startAutoOrders() // Start auto order placement

      setTimeout(() => {
        setLoading(false)
      }, 1000)
    } else {
      setLoading(false)
    }

    return () => {
      // Clean up all intervals and refs
      stopAutoManagement()
      stopRecommendationFetching()
      stopAutoOrders()
      stopAutoRefresh()
      
      // Clear any remaining intervals
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (autoManagementRef.current) {
        clearInterval(autoManagementRef.current)
        autoManagementRef.current = null
      }
      if (recommendationIntervalRef.current) {
        clearInterval(recommendationIntervalRef.current)
        recommendationIntervalRef.current = null
      }
      if (autoOrderIntervalRef.current) {
        clearInterval(autoOrderIntervalRef.current)
        autoOrderIntervalRef.current = null
      }
    }
  }, [user?.kiteConnected])

  // Handle recommendation interval changes
  useEffect(() => {
    if (autoRefreshEnabled && user?.kiteConnected) {
      // Restart auto refresh with new interval
      startAutoRefresh();
    }
  }, [recommendationInterval, autoRefreshEnabled, user?.kiteConnected]);

  // Handle fetch interval changes
  useEffect(() => {
    // Reset last fetch time when interval changes to allow immediate fetching
    setLastFetchTime(null);
    addLog(`⏰ Fetch interval changed to ${fetchInterval === 0.5 ? '30 seconds' : `${fetchInterval} minute(s)`}`, 'info');
    
    // Trigger immediate fetch with new interval
    if (user?.kiteConnected) {
      fetchStreamingRecommendations();
    }
  }, [fetchInterval]);

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    if (lastSignalGeneration && Date.now() - lastSignalGeneration.getTime() > 5 * 60 * 1000) {
      addLog('🔄 Signal generation timeout reached, refreshing data...')
      fetchIntradayData()
    }
  }, [lastSignalGeneration])

  // Auto-refresh when signals are generated
  useEffect(() => {
    if (lastSignalGeneration && user?.kiteConnected) {
      addLog('📊 Signals updated, refreshing intraday data...')
      fetchIntradayData()
    }
  }, [lastSignalGeneration])

  // Listen for streaming recommendations updates
  useEffect(() => {
    const handleStreamingUpdate = (event) => {
      const { growth, fall } = event.detail;
      addLog('🔄 Received new streaming recommendations, checking for Gemini analysis...', 'info');
      // --- Enhanced: Respect Time Window, append new stocks ---
      setStreamingRecommendations(prev => {
        // Helper to merge and deduplicate by symbol
        const mergeUnique = (oldArr, newArr) => {
          const map = new Map();
          [...oldArr, ...newArr].forEach(item => {
            if (item && item.symbol) map.set(item.symbol, item);
          });
          return Array.from(map.values());
        };
        // If time window is longer, keep previous stocks and add new ones
        return {
          growth: mergeUnique(prev.growth || [], growth || []),
          fall: mergeUnique(prev.fall || [], fall || [])
        };
      });

      // Only trigger Gemini analysis if we have both BUY and SELL recommendations AND autoManagementActive
      const mergedGrowth = streamingRecommendations.growth || [];
      const mergedFall = streamingRecommendations.fall || [];
      if (autoManagementActive && mergedGrowth.length > 0 && mergedFall.length > 0) {
        addLog('🤖 Triggering automatic Gemini analysis with complete recommendations...', 'info');
        const result = consultGeminiForOrder({ growth: mergedGrowth, fall: mergedFall });
        if (!result) {
          addLog('⚠️ Gemini analysis was not completed - missing BUY or SELL recommendations', 'warning');
        }
      } else if (!autoManagementActive) {
        addLog('⚠️ Gemini analysis skipped: Auto-management is paused.', 'warning');
      } else {
        addLog('⚠️ Skipping Gemini analysis - need both BUY and SELL recommendations. Current: BUY=' + (mergedGrowth.length || 0) + ', SELL=' + (mergedFall.length || 0), 'warning');
      }
    };

    window.addEventListener('streamingRecommendationsUpdated', handleStreamingUpdate);
    
    return () => {
      window.removeEventListener('streamingRecommendationsUpdated', handleStreamingUpdate);
    };
  }, []);

  const handleRefresh = () => {
    setRefreshing(true)
    addLog('🔄 Manual refresh triggered')
    fetchIntradayData()
  }

  // --- AUTO REFRESH, TIME WINDOW & SL BUTTON UI ---
  // Place this UI section near your auto-refresh controls in your render:
  // <div className="flex items-center gap-4 mb-2">
  //   <label className="font-semibold">Auto Refresh:</label>
  //   <input
  //     type="number"
  //     min={5}
  //     max={300}
  //     value={recommendationInterval}
  //     onChange={e => setRecommendationInterval(Number(e.target.value))}
  //     className="input input-sm w-20"
  //   />
  //   <span className="text-xs text-gray-500">seconds</span>
  //   <label className="font-semibold ml-6">Time Window:</label>
  //   <input
  //     type="number"
  //     min={0.5}
  //     max={30}
  //     step={0.5}
  //     value={fetchInterval}
  //     onChange={e => setFetchInterval(Number(e.target.value))}
  //     className="input input-sm w-20"
  //   />
  //   <span className="text-xs text-gray-500">minutes</span>
  //   <button
  //     className="btn btn-warning ml-6"
  //     onClick={handleManualPlaceSLOrders}
  //     title="Place SL Orders for all open positions"
  //   >
  //     Place SL Orders
  //   </button>
  // </div>
  const positionsColumns = [
    {
      key: 'tradingsymbol',
      header: 'Instrument',
      render: (position) => (
        <div className="flex items-center space-x-2">
          <span className="font-medium">{position.tradingsymbol}</span>
          <span className="badge badge-success text-xs">MIS</span>
          <button
            onClick={() => openTradingView(position.tradingsymbol, position.exchange)}
            className="text-blue-600 hover:text-blue-800"
            title="View on TradingView"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      )
    },
    {
      key: 'quantity',
      header: 'Quantity',
      accessor: (position) => position.quantity,
      render: (position) => (
        <span className={position.quantity > 0 ? 'text-success-600' : 'text-danger-600'}>
          {position.quantity > 0 ? '+' : ''}{position.quantity}
        </span>
      )
    },
    {
      key: 'average_price',
      header: 'Avg Price',
      accessor: (position) => position.average_price,
      render: (position) => `₹${position.average_price?.toFixed(2) || '0.00'}`
    },
    {
      key: 'last_price',
      header: 'LTP',
      accessor: (position) => position.last_price,
      render: (position) => `₹${position.last_price?.toFixed(2) || '0.00'}`
    },
    {
      key: 'pnl',
      header: 'P&L',
      accessor: (position) => position.pnl || 0,
      render: (position) => (
        <span className={(position.pnl || 0) >= 0 ? 'text-success-600' : 'text-danger-600'}>
          {(position.pnl || 0) >= 0 ? '+' : ''}₹{(position.pnl || 0).toFixed(2)}
        </span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (position) => (
        <span className="badge badge-success">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (position) => (
        <button
          onClick={() => exitPosition(position)}
          disabled={exitingPosition === position.tradingsymbol}
          className="btn-danger text-xs px-3 py-1 flex items-center space-x-1"
        >
          {exitingPosition === position.tradingsymbol ? (
            <LoadingSpinner size="small" />
          ) : (
            <LogOut className="w-3 h-3" />
          )}
          <span>Exit</span>
        </button>
      )
    }
  ]

  const ordersColumns = [
    {
      key: 'tradingsymbol',
      header: 'Instrument',
      render: (order) => (
        <div className="flex items-center space-x-2">
          <span className="font-medium">{order.tradingsymbol}</span>
          <span className="badge badge-success text-xs">MIS</span>
          <button
            onClick={() => openTradingView(order.tradingsymbol, order.exchange)}
            className="text-blue-600 hover:text-blue-800"
            title="View on TradingView"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      )
    },
    {
      key: 'transaction_type',
      header: 'Type',
      render: (order) => (
        <span className={`badge ${
          order.transaction_type === 'BUY' ? 'badge-success' : 'badge-danger'
        }`}>
          {order.transaction_type}
        </span>
      )
    },
    {
      key: 'order_type',
      header: 'Order Type',
      accessor: (order) => order.order_type,
      render: (order) => (
        <span className={`badge ${
          order.order_type === 'SL' ? 'badge-warning' : 'badge-info'
        }`}>
          {order.order_type}
          {order.order_type === 'SL' && (
            <Shield className="w-3 h-3 ml-1 inline" />
          )}
        </span>
      )
    },
    {
      key: 'quantity',
      header: 'Quantity',
      accessor: (order) => order.quantity
    },
    {
      key: 'price',
      header: 'Price',
      accessor: (order) => order.price,
      render: (order) => order.price ? `₹${order.price.toFixed(2)}` : 'Market'
    },
    {
      key: 'trigger_price',
      header: 'Trigger',
      accessor: (order) => order.trigger_price,
      render: (order) => order.trigger_price ? `₹${order.trigger_price.toFixed(2)}` : '-'
    },
    {
      key: 'status',
      header: 'Status',
      render: (order) => (
        <span className="badge badge-warning">
          {order.status}
        </span>
      )
    },
    {
      key: 'order_timestamp',
      header: 'Time',
      render: (order) => (
        <div className="flex items-center space-x-1 text-sm text-gray-600">
          <Clock className="w-4 h-4" />
          <span>{new Date(order.order_timestamp).toLocaleTimeString()}</span>
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (order) => (
        <button
          onClick={() => cancelOrder(order)}
          disabled={cancellingOrder === order.order_id}
          className="btn-danger text-xs px-3 py-1 flex items-center space-x-1"
        >
          {cancellingOrder === order.order_id ? (
            <LoadingSpinner size="small" />
          ) : (
            <X className="w-3 h-3" />
          )}
          <span>Cancel</span>
        </button>
      )
    }
  ]

  // --- WATCHLIST FETCHING ---
  useEffect(() => {
    const fetchUserWatchlists = async () => {
      try {
        const res = await watchlistAPI.getWatchlists();
        setWatchlists(res.data);
        if (res.data.length > 0) setSelectedWatchlist(res.data[0]);
      } catch (err) {
        addLog('❌ Failed to fetch watchlists', 'error');
      }
    };
    fetchUserWatchlists();
  }, []);

  // --- RENDER CONDITIONS ---
  if (!user?.kiteConnected) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Kite Not Connected</h2>
        <p className="text-gray-600 mb-6">
          Please connect your Kite account in Settings to view intraday trading.
        </p>
        <a href="/settings" className="btn-primary">
          Go to Settings
        </a>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  // More robust margin calculation
  const availableMargin = marginCalculations.availableMargin || 0
  const usedMargin = data.margins?.equity?.used?.debits || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Robust Intraday Trading</h1>
          <p className="text-sm text-gray-600 mt-1">MIS Orders Only • Enhanced Safety • Duplicate Prevention</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Watchlist Selector */}
          <div className="flex items-center space-x-2">
            <label htmlFor="watchlist" className="text-sm font-medium text-gray-700">Watchlist:</label>
            <select
              id="watchlist"
              value={selectedWatchlist?._id || ''}
              onChange={e => {
                const wl = watchlists.find(w => w._id === e.target.value);
                setSelectedWatchlist(wl);
              }}
              className="border rounded px-2 py-1 text-sm min-w-32"
            >
              {watchlists.map(wl => (
                <option key={wl._id} value={wl._id}>{wl.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={autoManagementActive ? stopAutoManagement : startAutoManagement}
            className={`btn flex items-center space-x-2 ${
              autoManagementActive ? 'btn-danger' : 'btn-primary'
            }`}
          >
            {autoManagementActive ? (
              <>
                <Pause className="w-4 h-4" />
                <span>Stop Auto</span>
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                <span>Start Auto</span>
              </>
            )}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>
      
      {/* Watchlist Warning */}
      {!selectedWatchlist && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            <span className="text-yellow-800 text-sm">
              Please select a watchlist to enable LTP/volume fetching for order calculations.
            </span>
          </div>
        </div>
      )}

      {/* Order Placement Controls */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Order Placement</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {lastAutoOrderTime ? `Last Auto: ${lastAutoOrderTime.toLocaleTimeString()}` : 'No auto orders yet'}
            </span>
          </div>
        </div>
        
        {/* Margin Information */}
        {data.margins?.equity?.available && marginCalculations.availableMargin > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">Available Margin:</span>
                <span className="ml-2 text-blue-600">₹{formatCurrency(marginCalculations.availableMargin)}</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Boosted Margin (5X):</span>
                <span className="ml-2 text-blue-600">₹{formatCurrency(marginCalculations.boostedMargin)}</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Max Order Amount:</span>
                <span className="ml-2 text-blue-600">₹{formatCurrency(marginCalculations.orderAmount)}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Auto Refresh, Time Window, and SL Button Controls */}
        <div className="mb-4 p-3 bg-green-50 rounded flex items-center gap-4">
          <label className="font-semibold">Auto Refresh:</label>
          <select
            value={recommendationInterval}
            onChange={e => setRecommendationInterval(Number(e.target.value))}
            className="input input-sm w-24"
          >
            <option value={30}>30 s</option>
            <option value={60}>1 m</option>
            <option value={90}>1.5 m</option>
            <option value={120}>2 m</option>
            <option value={150}>2.5 m</option>
            <option value={180}>3 m</option>
          </select>
          <span className="text-xs text-gray-500"></span>
          <label className="font-semibold ml-6">Time Window:</label>
          <select
            value={fetchInterval}
            onChange={e => setFetchInterval(Number(e.target.value))}
            className="input input-sm w-24"
          >
            <option value={0.5}>30 s</option>
            <option value={1}>1 m</option>
            <option value={2}>2 m</option>
            <option value={3}>3 m</option>
            <option value={4}>4 m</option>
            <option value={5}>5 m</option>
            <option value={6}>6 m</option>
            <option value={7}>7 m</option>
            <option value={8}>8 m</option>
            <option value={9}>9 m</option>
            <option value={10}>10 m</option>
            <option value={11}>11 m</option>
            <option value={12}>12 m</option>
            <option value={13}>13 m</option>
            <option value={14}>14 m</option>
            <option value={15}>15 m</option>
          </select>
          <span className="text-xs text-gray-500"></span>
          <button
            className="btn-danger flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleManualPlaceSLOrders}
            title="Place SL Orders for all open positions"
          >
            Place SL Orders
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Auto Order Controls */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
            <div>
              <h4 className="font-medium text-blue-800">Auto Orders</h4>
              <p className="text-xs text-blue-600">Places BUY orders every 2 minutes using Gemini analysis</p>
            </div>
            <button
              onClick={() => {
                setAutoOrderEnabled(!autoOrderEnabled);
                if (!autoOrderEnabled) {
                  startAutoOrders();
                } else {
                  stopAutoOrders();
                }
              }}
              className={`text-xs px-3 py-1 rounded ${
                autoOrderEnabled 
                  ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                  : 'bg-gray-100 text-gray-800 border border-gray-300'
              }`}
            >
              {autoOrderEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {/* Manual BUY Orders */}
          <button
            onClick={placeManualBuyOrders}
            disabled={!geminiResult?.bestBuy?.symbol}
            className="btn-success flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Place Buy Orders</span>
          </button>

          {/* Manual SELL Orders */}
          <button
            onClick={placeManualSellOrders}
            disabled={!geminiResult?.bestSell?.symbol}
            className="btn-danger flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrendingDown className="w-4 h-4" />
            <span>Place Sell Orders</span>
          </button>
        </div>
      </div>

      {/* Streaming Recommendations & Gemini AI Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Streaming Recommendations */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Streaming Recommendations</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                {lastRecommendationFetch ? `Last: ${lastRecommendationFetch.toLocaleTimeString()}` : 'Not fetched yet'}
              </span>
              <button
                onClick={fetchStreamingRecommendations}
                className="btn-secondary text-xs px-2 py-1"
              >
                <RefreshCw className="w-3 h-3" />
              </button>
            </div>
          </div>
          
          {/* Auto Refresh Controls */}
          <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded">
            
            <button
              onClick={() => {
                setAutoRefreshEnabled(!autoRefreshEnabled);
                if (!autoRefreshEnabled) {
                  startAutoRefresh();
                } else {
                  stopAutoRefresh();
                }
              }}
                           className={`text-xs px-3 py-1 rounded ${
                autoRefreshEnabled 
                  ? 'bg-green-100 text-green-800 border border-green-300' 
                  : 'bg-gray-100 text-gray-800 border border-gray-300'
              }`}
            >
              {autoRefreshEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Growth Recommendations */}
            <div>
              <h4 className="font-semibold mb-2 text-success-700 flex items-center">
                <TrendingUp className="w-4 h-4 mr-1" />
                Growth ({streamingRecommendations.growth?.length || 0})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {streamingRecommendations.growth?.length > 0 ? (
                  streamingRecommendations.growth.map((rec, index) => (
                    <div key={index} className="p-2 bg-success-50 rounded text-sm">
                      <div className="font-medium text-success-800">{rec.symbol}</div>
                      <div className="text-success-600">
                        Score: {rec.slope ? rec.slope.toFixed(4) : (rec.score ? rec.score.toFixed(4) : '-')}
                      </div>
                      <div className="text-xs text-success-500">
                        LTP: ₹{rec.ltp?.toFixed(2) || '-'} | Change: {rec.pctChange?.toFixed(2) || '-'}%
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 text-sm">No growth recommendations</div>
                )}
              </div>
            </div>

            {/* Fall Recommendations */}
            <div>
              <h4 className="font-semibold mb-2 text-danger-700 flex items-center">
                <TrendingDown className="w-4 h-4 mr-1" />
                Fall ({streamingRecommendations.fall?.length || 0})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {streamingRecommendations.fall?.length > 0 ? (
                  streamingRecommendations.fall.map((rec, index) => (
                    <div key={index} className="p-2 bg-danger-50 rounded text-sm">
                      <div className="font-medium text-danger-800">{rec.symbol}</div>
                      <div className="text-danger-600">
                        Score: {rec.slope ? rec.slope.toFixed(4) : (rec.score ? rec.score.toFixed(4) : '-')}
                      </div>
                      <div className="text-xs text-danger-500">
                        LTP: ₹{rec.ltp?.toFixed(2) || '-'} | Change: {rec.pctChange?.toFixed(2) || '-'}%
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 text-sm">No fall recommendations</div>
                )}
              </div>
            </div>
          </div>
          
          {/* No Recommendations Message */}
          {(!streamingRecommendations.growth?.length && !streamingRecommendations.fall?.length) && (
            <div className="text-center py-4 text-gray-500">
              <p className="text-sm">No valid recommendations available</p>
              <p className="text-xs mt-1">Ensure you have valid stock data and active streaming</p>
            </div>
          )}
        </div>

        {/* Gemini AI Results */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Gemini AI Analysis</h3>
            <div className="flex items-center space-x-2">
              {geminiLastRun && (
                <span className="text-sm text-gray-500">
                  Last: {geminiLastRun.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={() => {
                  if (streamingRecommendations.growth?.length > 0 && streamingRecommendations.fall?.length > 0) {
                    consultGeminiForOrder(streamingRecommendations);
                  } else {
                    toast.error('Both BUY and SELL recommendations are required for Gemini analysis');
                    addLog('⚠️ Manual Gemini analysis skipped - need both BUY and SELL recommendations', 'warning');
                  }
                }}
                disabled={geminiLoading || (!streamingRecommendations.growth?.length || !streamingRecommendations.fall?.length)}
                className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {geminiLoading ? (
                  <LoadingSpinner size="small" />
                ) : (
                  <Activity className="w-3 h-3" />
                )}
                <span className="ml-1">Analyze</span>
              </button>
            </div>
          </div>

          {geminiLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="medium" />
              <span className="ml-2 text-primary-600">Analyzing with Gemini AI...</span>
            </div>
          ) : geminiResult && !geminiResult.error ? (
            <div className="space-y-4">
              {/* Best Buy Recommendation */}
              {geminiResult.bestBuy && (
                <div className="p-3 bg-success-50 border border-success-200 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-success-800">Best Buy</h4>
                    <span className="badge badge-success">BUY</span>
                  </div>
                  <div className="text-success-700 font-medium">{geminiResult.bestBuy.symbol}</div>
                  <div className="text-sm text-success-600 mt-1">{geminiResult.bestBuy.reason}</div>
                </div>
              )}

              {/* Best Sell Recommendation */}
              {geminiResult.bestSell && (
                <div className="p-3 bg-danger-50 border border-danger-200 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-danger-800">Best Sell</h4>
                    <span className="badge badge-danger">SELL</span>
                  </div>
                  <div className="text-danger-700 font-medium">{geminiResult.bestSell.symbol}</div>
                  <div className="text-sm text-danger-600 mt-1">{geminiResult.bestSell.reason}</div>
                </div>
              )}

              {/* Place Orders Button */}
              {geminiResult.bestBuy || geminiResult.bestSell ? (
                <button
                  onClick={() => placeOrdersFromGemini(geminiResult)}
                  className="btn-success w-full flex items-center justify-center space-x-2"
                >
                  <Play className="w-4 h-4" />
                  <span>Place Gemini Orders</span>
                </button>
              ) : (
                <div className="text-gray-500 text-center py-4">No actionable recommendations</div>
              )}
            </div>
          ) : geminiResult && geminiResult.error ? (
            <div className="p-3 bg-danger-50 border border-danger-200 rounded">
              <div className="text-danger-800 font-medium">Gemini Analysis Error</div>
              <div className="text-danger-600 text-sm">{geminiResult.error}</div>
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">
              <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No Gemini analysis yet</p>
              <p className="text-xs mt-1">Click "Analyze" to consult Gemini AI</p>
              {(!streamingRecommendations.growth?.length && !streamingRecommendations.fall?.length) && (
                <p className="text-xs mt-2 text-gray-400">No valid recommendations available for analysis</p>
              )}
            </div>
          )}
        </div>
      </div>

      

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="card border-danger-200 bg-danger-50">
          <div className="flex items-center space-x-2 mb-2">
            <XCircle className="w-5 h-5 text-danger-600" />
            <h3 className="font-semibold text-danger-800">Validation Errors</h3>
          </div>
          <ul className="text-sm text-danger-700 space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
          <button
            onClick={() => setValidationErrors([])}
            className="text-xs text-danger-600 hover:text-danger-800 mt-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Activity Log */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Robust Activity Log</h3>
          <button
            onClick={() => setIntradayLogs([])}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {intradayLogs.map((log) => (
            <div key={log.id} className="flex items-start space-x-3 p-2 bg-gray-50 rounded text-sm">
              <div className={`w-2 h-2 rounded-full mt-2 ${
                log.type === 'success' ? 'bg-success-500' :
                log.type === 'warning' ? 'bg-yellow-500' :
                log.type === 'error' ? 'bg-danger-500' : 'bg-blue-500'
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-gray-900">{log.message}</p>
                <p className="text-xs text-gray-500">
                  {log.timestamp.toLocaleTimeString()}
                </p>
              </div>
            </div>
          ))}
          {intradayLogs.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              <p className="text-sm">No activity logs yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Available Margin</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{availableMargin.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="text-sm text-gray-600">
              Used: ₹{usedMargin.toLocaleString('en-IN')}
            </span>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Day's P&L (MIS Only)</p>
              <p className={`text-2xl font-bold ${
                data.dayPnL >= 0 ? 'text-success-600' : 'text-danger-600'
              }`}>
                {data.dayPnL >= 0 ? '+' : ''}₹{data.dayPnL.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              {data.dayPnL >= 0 ? (
                <TrendingUp className="w-6 h-6 text-success-600" />
              ) : (
                <TrendingDown className="w-6 h-6 text-danger-600" />
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Robust Auto Management</p>
              <p className={`text-lg font-bold ${
                autoManagementActive ? 'text-success-600' : 'text-gray-900'
              }`}>
                {autoManagementActive ? 'Active' : 'Inactive'}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-primary-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="text-sm text-gray-600">
              Active Positions: {data.positions.length} | Active Orders: {data.orders.length}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active MIS Positions */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Active MIS Positions</h3>
            {data.positions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No active MIS positions today</p>
              </div>
            ) : (
              <SortableTable
                data={data.positions}
                columns={positionsColumns}
                searchPlaceholder="Search active positions..."
              />
            )}
          </div>

          {/* Active MIS Orders */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Active MIS Orders</h3>
            {data.orders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No active MIS orders</p>
              </div>
            ) : (
              <SortableTable
                data={data.orders}
                columns={ordersColumns}
                searchPlaceholder="Search active orders..."
              />
            )}
          </div>
        </div>
      </div>

    </div>
  )
}

export default IntradayRobust